import { useState } from "react"
import type { FormEvent } from "react"
import type { Locale } from "../../content/types"
import { contactContent, socialLinks } from "../../content/contact"
import { company, formatDisplayLocation } from "../../content/company"

interface ContactPageProps {
  locale: Locale
}

export default function ContactPage({ locale }: ContactPageProps) {
  const t = contactContent
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyField: "",
    address: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")

  // Sanitize input to prevent XSS and injection attacks
  const sanitizeInput = (input: string): string => {
    return input
      .replace(/[<>]/g, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+=/gi, "")
      // eslint-disable-next-line no-control-regex -- intentional: strip control characters from user input
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
      .trim()
      .slice(0, 5000)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        phone: sanitizeInput(formData.phone),
        company: sanitizeInput(formData.companyField),
        address: sanitizeInput(formData.address),
        message: sanitizeInput(formData.message),
      }

      const apiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "") || ""
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sanitizedData),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setSubmitStatus("success")
        setFormData({ name: "", email: "", phone: "", companyField: "", address: "", message: "" })
      } else {
        throw new Error(data.error || "Failed to send message")
      }
    } catch (error) {
      console.error("Error submitting form:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
      setTimeout(() => setSubmitStatus("idle"), 5000)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <div className="min-h-screen bg-site-bg">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 pt-16 pb-8">
        <div className="text-center max-w-3xl mx-auto relative">
          <h1 className="text-5xl md:text-6xl font-bold text-text-primary mb-4">{t.title[locale]}</h1>
          <p className="text-xl text-text-secondary font-medium mb-2">{t.subtitle[locale]}</p>
          <p className="text-base text-text-secondary">{t.description[locale]}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-2">
            <div className="sticky top-32">
              <h2 className="text-2xl font-bold text-text-primary mb-8 flex items-center gap-2">{t.info.title[locale]}</h2>

              <div className="space-y-8">
                {/* Email */}
                <div className="group">
                  <p className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">{t.info.email[locale]}</p>
                  <a
                    href={`mailto:${company.email}`}
                    className="text-lg font-medium text-text-primary hover:text-button-primary transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {company.email}
                  </a>
                </div>

                {/* Phone Numbers */}
                <div className="group">
                  <p className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">{t.info.phone[locale]}</p>
                  <div className="space-y-2">
                    {company.phoneNumbers.map((phone) => (
                      <a
                        key={phone.href}
                        href={phone.href}
                        className="text-lg font-medium text-text-primary hover:text-button-primary transition-colors flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {phone.display}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Social Media */}
                <div className="group">
                  <p className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">{t.info.social[locale]}</p>
                  <div className="flex flex-col gap-3 items-start">
                    {socialLinks().map((social) => (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-3 px-5 py-3 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-md w-48"
                        style={{ backgroundColor: social.color }}
                      >
                        <span className="font-medium">{social.label}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Location */}
                <div className="group">
                  <p className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">{t.info.location[locale]}</p>
                  <div className="flex items-start gap-2 text-text-primary">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="text-lg font-medium">
                      <p>{formatDisplayLocation(locale)}</p>
                    </div>
                  </div>
                </div>

                {/* Response time */}
                <div className="mt-12 pt-8 border-t border-border-subtle">
                  <div className="flex items-center gap-3 text-text-secondary">
                    <span className="text-4xl" aria-hidden="true">☕</span>
                    <p className="text-sm italic">{t.info.responseTime[locale]}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-card-bg rounded-[var(--radius-card)] p-8 md:p-10 shadow-lg border border-border-subtle">
              <h2 className="text-3xl font-bold text-text-primary mb-6">{t.form.title[locale]}</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-text-primary mb-2">
                    {t.form.name[locale]} <span className="text-button-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.form.namePlaceholder[locale]}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border-subtle focus:border-button-primary focus:outline-none transition-colors bg-white text-text-primary"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-text-primary mb-2">
                    {t.form.email[locale]} <span className="text-button-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.form.emailPlaceholder[locale]}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border-subtle focus:border-button-primary focus:outline-none transition-colors bg-white text-text-primary"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-text-primary mb-2">
                      {t.form.phone[locale]}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t.form.phonePlaceholder[locale]}
                      className="w-full px-4 py-3 rounded-xl border-2 border-border-subtle focus:border-button-primary focus:outline-none transition-colors bg-white text-text-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="companyField" className="block text-sm font-semibold text-text-primary mb-2">
                      {t.form.company[locale]}
                    </label>
                    <input
                      type="text"
                      id="companyField"
                      name="companyField"
                      value={formData.companyField}
                      onChange={handleChange}
                      placeholder={t.form.companyPlaceholder[locale]}
                      className="w-full px-4 py-3 rounded-xl border-2 border-border-subtle focus:border-button-primary focus:outline-none transition-colors bg-white text-text-primary"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-text-primary mb-2">
                    {t.form.address[locale]}
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t.form.addressPlaceholder[locale]}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border-subtle focus:border-button-primary focus:outline-none transition-colors bg-white text-text-primary"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-text-primary mb-2">
                    {t.form.message[locale]} <span className="text-button-primary">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder={t.form.messagePlaceholder[locale]}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border-subtle focus:border-button-primary focus:outline-none transition-colors bg-white text-text-primary resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-button-primary text-white font-bold py-4 px-8 rounded-xl hover:bg-[#c55b01] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] shadow-lg"
                >
                  {isSubmitting ? t.form.sending[locale] : t.form.submit[locale]}
                </button>

                {submitStatus === "success" && (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                    <p className="text-green-800 font-medium flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {t.form.success[locale]}
                    </p>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                    <p className="text-red-800 font-medium flex items-center gap-2">
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {t.form.error[locale]}
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
